import { useCallback, useEffect, useState } from "react";
import { Truck, KeyRound, X } from "lucide-react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none";

export default function AdminDirectCouriers() {
  const [couriers, setCouriers] = useState(null);
  const [configuring, setConfiguring] = useState(null);
  const [creds, setCreds] = useState({ api_key: "", api_secret: "", account_id: "" });
  const [note, setNote] = useState("");

  const load = useCallback(() => api.get("/admin/direct-couriers").then(({ data }) => setCouriers(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const configure = async () => {
    try {
      await api.post(`/admin/direct-couriers/${configuring.code}/configure`, { credentials: creds, note });
      toast.success(`${configuring.name} credentials stored (server-side)`);
      setConfiguring(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const activate = async (c) => {
    try {
      await api.post(`/admin/direct-couriers/${c.code}/activate`, { active: !c.active });
      toast.success(`${c.name} ${c.active ? "deactivated" : "activated"}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="admin-direct-couriers-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Direct Couriers</h1>
      <p className="text-sm text-slate-500">Direct carrier adapter registry. Adapters activate only with official API documentation + credentials — no invented endpoints.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="direct-couriers-grid">
        {(couriers || []).map((c) => (
          <div key={c.code} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`direct-courier-${c.code}`}>
            <div className="flex items-center justify-between">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-shipany-navy text-shipany-cyan"><Truck className="h-5 w-5" /></span>
              <span className="mono rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{c.region}</span>
            </div>
            <h3 className="font-display mt-3 font-bold text-shipany-navy">{c.name}</h3>
            <p className="mt-1 text-xs text-slate-400">{c.note}</p>
            <div className="mt-3 flex items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${c.active ? "bg-emerald-50 text-emerald-600" : c.configured ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-400"}`}>
                {c.active ? "ACTIVE" : c.configured ? "CONFIGURED" : "PENDING"}
              </span>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => { setConfiguring(c); setNote(c.note || ""); setCreds({ api_key: "", api_secret: "", account_id: "" }); }} data-testid={`configure-${c.code}`}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-xs font-semibold text-shipany-blue hover:bg-shipany-blue/5">
                <KeyRound className="h-3.5 w-3.5" /> Credentials
              </button>
              <button onClick={() => activate(c)} data-testid={`activate-${c.code}`}
                className={`flex-1 rounded-lg py-2 text-xs font-bold ${c.active ? "bg-red-50 text-red-600" : "bg-shipany-blue text-white hover:bg-[#0648B3]"}`}>
                {c.active ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {configuring && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="direct-courier-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setConfiguring(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold text-shipany-navy">{configuring.name} credentials</h2><button onClick={() => setConfiguring(null)} className="text-slate-400"><X className="h-5 w-5" /></button></div>
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-700">Stored encrypted server-side. Activation requires verified official API docs — bookings route via aggregators until then.</p>
            <div className="mt-4 space-y-3.5">
              <input data-testid="dc-api-key" type="password" className={`${inputCls} mono`} placeholder="API key / token" value={creds.api_key} onChange={(e) => setCreds({ ...creds, api_key: e.target.value })} />
              <input data-testid="dc-api-secret" type="password" className={`${inputCls} mono`} placeholder="API secret (if any)" value={creds.api_secret} onChange={(e) => setCreds({ ...creds, api_secret: e.target.value })} />
              <input data-testid="dc-account" className={`${inputCls} mono`} placeholder="Account / client ID (if any)" value={creds.account_id} onChange={(e) => setCreds({ ...creds, account_id: e.target.value })} />
              <input data-testid="dc-note" className={inputCls} placeholder="Note (docs link, contact)" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <button onClick={configure} data-testid="dc-save-btn" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Save Credentials</button>
          </div>
        </div>
      )}
    </div>
  );
}
